import React, { useMemo } from 'react';
import { useWorkspaceMerge } from '../../../contexts/WorkspaceMergeContext';

function parseNum(raw) {
  if (raw == null || raw === '') return 0;
  const cleaned = String(raw).replace(/[$,]/g, '');
  const val = parseFloat(cleaned);
  return isNaN(val) ? 0 : val;
}

function fmt(n) {
  return '$' + Math.round(n).toLocaleString('en-US');
}

function LineItem({ label, value }) {
  return (
    <div className="flex justify-between items-center py-1.5">
      <span className="text-xs text-gray-500">{label}</span>
      <span className="text-sm font-semibold text-gray-800 tabular-nums">{fmt(value)}</span>
    </div>
  );
}

export default function CoverageCalculator() {
  const { intake_data } = useWorkspaceMerge();

  const calc = useMemo(() => {
    const mortgageBalance = parseNum(intake_data.mortgageBalance);
    const carLoans = parseNum(intake_data.carLoans);
    const creditCards = parseNum(intake_data.creditCards);
    const otherDebt = parseNum(intake_data.otherDebt);
    const totalDebt = mortgageBalance + carLoans + creditCards + otherDebt;

    const annualIncome = parseNum(intake_data.annualIncome);
    const incomeYears = parseNum(intake_data.incomeYears);
    const incomeReplacement = parseNum(intake_data.incomeReplacement) || (annualIncome * (incomeYears || 10));

    const numberOfKids = parseNum(intake_data.numberOfKids);
    const educationPerChild = parseNum(intake_data.educationPerChild);
    const educationTotal = parseNum(intake_data.educationTotal) || (numberOfKids * (educationPerChild || 50000));

    const fexCoverageAmount = parseNum(intake_data.fexCoverageAmount);
    const isFexPath = fexCoverageAmount > 0 && totalDebt === 0 && incomeReplacement === 0;

    const totalInsurableNeed = isFexPath
      ? fexCoverageAmount
      : totalDebt + incomeReplacement + educationTotal;

    const currentCoverage = parseNum(intake_data.currentCoverage);
    const coverageGap = totalInsurableNeed - currentCoverage;

    return { totalDebt, incomeReplacement, educationTotal, fexCoverageAmount, isFexPath, totalInsurableNeed, currentCoverage, coverageGap };
  }, [intake_data]);

  const hasData = calc.totalInsurableNeed > 0;

  const gapSection = calc.currentCoverage > 0 && (
    <>
      <div className="flex justify-between items-center py-1.5">
        <span className="text-xs text-gray-500">Current Coverage</span>
        <span className="text-sm font-semibold text-gray-800 tabular-nums">{fmt(calc.currentCoverage)}</span>
      </div>
      <div className="border-t border-gray-200 my-2" />
      <div className="flex justify-between items-center pt-1">
        <span className="text-xs font-bold text-gray-600 uppercase">
          {calc.coverageGap > 0 ? 'Coverage Gap' : 'Coverage Surplus'}
        </span>
        <span
          className="text-lg font-bold tabular-nums"
          style={{ color: calc.coverageGap > 0 ? '#ef4444' : '#22c55e' }}
        >
          {fmt(Math.abs(calc.coverageGap))}
        </span>
      </div>
    </>
  );

  return (
    <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
        <h3 className="text-xs font-bold text-gray-600 uppercase tracking-wider">Coverage Calculator</h3>
      </div>
      <div className="p-4">
        {!hasData ? (
          <p className="text-xs text-gray-400 text-center py-3">
            Enter financial data in the intake fields to calculate coverage needs.
          </p>
        ) : calc.isFexPath ? (
          <>
            <LineItem label="FEX Coverage Amount" value={calc.fexCoverageAmount} />
            <div className="border-t border-gray-200 my-2" />
            <div className="flex justify-between items-center pt-1">
              <span className="text-xs font-bold text-gray-600 uppercase">Total Coverage</span>
              <span className="text-lg font-bold tabular-nums" style={{ color: '#00AAFF' }}>
                {fmt(calc.totalInsurableNeed)}
              </span>
            </div>
            {gapSection}
          </>
        ) : (
          <>
            <LineItem label="Total Debt" value={calc.totalDebt} />
            <LineItem label="Income Replacement" value={calc.incomeReplacement} />
            <LineItem label="Education Fund" value={calc.educationTotal} />
            <div className="border-t border-gray-200 my-2" />
            <div className="flex justify-between items-center pt-1">
              <span className="text-xs font-bold text-gray-600 uppercase">Total Insurable Need</span>
              <span className="text-lg font-bold tabular-nums" style={{ color: '#00AAFF' }}>
                {fmt(calc.totalInsurableNeed)}
              </span>
            </div>
            {gapSection}
          </>
        )}
      </div>
    </div>
  );
}
